import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { regions as mockRegions } from '../data/mock';
import { getRegions } from '../services/api';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const RegionsSection = () => {
  const [regions, setRegions] = useState(mockRegions);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) {
      return obj[`${field}_en`];
    }
    return obj[field] || '';
  };

  useEffect(() => {
    const fetchRegions = async () => {
      const data = await getRegions();
      if (data && data.length > 0) {
        setRegions(data);
      }
      setLoading(false);
    };
    
    fetchRegions();
  }, []);

  // Show only first 3 regions as preview
  const previewRegions = regions.slice(0, 3);

  return (
    <section id="regions" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-display font-light text-brand-text mb-6">{t.regionsTitle}</h2>
          <p className="text-lg font-body text-brand-text-secondary max-w-4xl mx-auto leading-relaxed">
            {t.regionsSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 max-w-7xl mx-auto">
          {previewRegions.map((region) => (
            <Card key={region.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border border-brand-accent/30">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img 
                  src={region.image} 
                  alt={getTranslated(region, 'name')}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <CardContent className="p-8 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-display font-medium text-brand-text">{getTranslated(region, 'name')}</h3>
                  {region.priceRange && (
                    <span className="text-xs font-body tracking-wider text-brand-primary bg-brand-soft px-4 py-2 rounded-full">
                      {region.priceRange}
                    </span>
                  )}
                </div>
                <p className="text-brand-text-secondary font-body leading-relaxed">{getTranslated(region, 'description')}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Botão Saiba Mais */}
        <div className="text-center mb-16">
          <Link 
            to="/regioes"
            className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-body font-medium text-lg transition-colors group"
          >
            {language === 'en' ? 'Explore All Regions' : 'Explorar Todas as Regiões'}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="text-center max-w-4xl mx-auto">
          <p className="text-lg font-body text-brand-text-secondary italic leading-relaxed">
            {language === 'pt' 
              ? "Cada uma destas regiões oferece oportunidades únicas. Conte connosco para selecionar os imóveis certos para si e organizar uma experiência de compra transparente e personalizada."
              : "Each of these regions offers unique opportunities. Count on us to select the right properties for you and organize a transparent and personalized purchase experience."
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default RegionsSection;